import { useMutation } from '@apollo/client/react';
import { Alert, Button, Form } from 'antd';
import { forEach, includes, keys } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../../../../../AppContext';
import { toast } from '../../../../../../../../apollo';
import {
  ADDONS,
  BULK_ACTION,
  BULK_JOB_STATUS,
} from '../../../../../../../../common/constants';
import CommonBulkConfirmationModal from '../../../../../../../../components/CommonBulkConfirmationModal';
import CommonDrawer from '../../../../../../../../components/CommonDrawer';
import { BULK_UPDATE_PROJECT_EQC_TYPE_STAGE_APPROVERS } from '../../../../../../graphql/Mutation';
import ApproverLevels from './ApproverLevels';

const BulkAssignApproversDrawer = ({
  showDrawer,
  setShowDrawer,
  selectedRows,
  isSelectAllAcrossPages,
  exceptIds,
  paginationProp,
  rfiStageCount,
  getBulkProjectStageJobStatus,
  startJobTracking,
  onSuccess,
  projectEqcTypeId,
  projectId,
}) => {
  const { getTenantUser } = useContext(AppContext);
  const tenantUser = getTenantUser();
  const hasMultiLevelApproval = includes(
    tenantUser?.tenant?.addOn,
    ADDONS?.MULTI_LEVEL_APPROVAL,
  );
  const maxApprovalLevel = tenantUser?.tenant?.maxApprovalLevel || 1;

  const [form] = Form.useForm();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [allowedLevels] = useState(
    hasMultiLevelApproval
      ? Array.from({ length: maxApprovalLevel }, (_, i) => i + 1)
      : [1],
  );
  const [highlightedLevels, setHighlightedLevels] = useState([]);

  const selectedCount = isSelectAllAcrossPages
    ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
    : selectedRows?.length || 0;

  const showRfiApprovers = isSelectAllAcrossPages
    ? rfiStageCount > 0
    : selectedRows?.some((row) => row?.rfiStage && row?.isRfiActive);

  const selectedStageIds = selectedRows?.map((row) => row?.id) || [];

  useEffect(() => {
    form?.setFieldsValue({ needApproval: true, rfiNeedApproval: true });
  }, [form]);

  const [bulkUpdateApprovers, { loading }] = useMutation(
    BULK_UPDATE_PROJECT_EQC_TYPE_STAGE_APPROVERS,
    {
      onCompleted(response) {
        const { status, jobId } =
          response?.bulkUpdateProjectEqcTypeStageApprovers || {};

        if (status === BULK_JOB_STATUS.SUCCESS && jobId) {
          startJobTracking(jobId, BULK_ACTION.EDIT_APPROVERS);
          getBulkProjectStageJobStatus({
            variables: { jobId },
          });
        }

        onSuccess?.();
        setShowDrawer(false);
      },
      onError(error) {
        const requiredLevels =
          error?.errors?.[0]?.extensions?.exception?.requiredLevels;
        if (requiredLevels?.length > 0) {
          const missingLevels = [
            ...new Set(
              requiredLevels?.map(
                (requiredLevel) => requiredLevel?.missingLevel,
              ),
            ),
          ];
          setHighlightedLevels(missingLevels);
        }
        setApiError(error?.message);
      },
    },
  );

  const getTeamInfo = (teamInfoData) => ({
    selectedAgencyIds: teamInfoData?.selectedAgencyIds || [],
    excludeIds: teamInfoData?.excludeIds || [],
    individualUserIds: teamInfoData?.individualUserIds || [],
  });

  const buildApprovalLevelData = () => {
    const allValues = form?.getFieldsValue(true) || {};
    const currentAllowedLevels = allowedLevels || [];

    const approvalLevelData = [];

    forEach(keys(allValues?.approvers || {}), (levelKey) => {
      const levelNumber = Number(levelKey);
      if (!includes(currentAllowedLevels, levelNumber)) return;

      const levelData = allValues?.approvers?.[levelKey] || {};
      const internalIds = levelData?.internalApproverIds || [];
      const externalIds = levelData?.externalUserApproverIds || [];
      const isInternalSelectAll = includes(internalIds, 'all');
      const isExternalSelectAll = includes(externalIds, 'all');
      const internalInfo = getTeamInfo(levelData?.internalTeamInfo);
      const externalInfo = getTeamInfo(levelData?.externalTeamInfo);

      const hasSelections =
        isInternalSelectAll ||
        isExternalSelectAll ||
        internalInfo?.individualUserIds?.length > 0 ||
        internalInfo?.selectedAgencyIds?.length > 0 ||
        externalInfo?.individualUserIds?.length > 0 ||
        externalInfo?.selectedAgencyIds?.length > 0 ||
        levelData?.approvalType;

      if (!hasSelections) return;

      const level = {
        levelNumber,
        levelName: levelData?.levelName || `Level ${levelNumber}`,
        approvalType: levelData?.approvalType,
      };

      if (isInternalSelectAll) {
        level.selectedAllInternalApprovers = true;
      } else {
        level.internalApproverIds = internalInfo?.individualUserIds;
        level.selectedInternalApproverAgencyIds =
          internalInfo?.selectedAgencyIds;
        level.excludeInternalApproverIds = internalInfo?.excludeIds;
      }

      if (isExternalSelectAll) {
        level.selectedAllExternalApprovers = true;
      } else {
        level.externalUserApproverIds = externalInfo?.individualUserIds;
        level.selectedExternalApproverAgencyIds =
          externalInfo?.selectedAgencyIds;
        level.excludeExternalUserApproverIds = externalInfo?.excludeIds;
      }

      approvalLevelData.push(level);
    });

    return approvalLevelData;
  };

  const buildRfiApprovalLevelData = () => {
    const allValues = form?.getFieldsValue(true) || {};
    const rfiApprover = allValues?.rfiApprover;

    if (!rfiApprover) return [];

    const rfiInternalIds = rfiApprover?.internalApproverIds || [];
    const rfiExternalIds = rfiApprover?.externalUserApproverIds || [];
    const isRfiInternalSelectAll = includes(rfiInternalIds, 'all');
    const isRfiExternalSelectAll = includes(rfiExternalIds, 'all');
    const internalInfo = getTeamInfo(rfiApprover?.internalTeamInfo);
    const externalInfo = getTeamInfo(rfiApprover?.externalTeamInfo);

    const hasSelections =
      isRfiInternalSelectAll ||
      isRfiExternalSelectAll ||
      internalInfo?.individualUserIds?.length > 0 ||
      internalInfo?.selectedAgencyIds?.length > 0 ||
      externalInfo?.individualUserIds?.length > 0 ||
      externalInfo?.selectedAgencyIds?.length > 0;

    if (!hasSelections) return [];

    const level = {
      levelNumber: 1,
      levelName: rfiApprover?.levelName || 'RFI Approver',
    };

    if (isRfiInternalSelectAll) {
      level.selectedAllInternalApprovers = true;
    } else {
      level.internalApproverIds = internalInfo?.individualUserIds;
      level.selectedInternalApproverAgencyIds = internalInfo?.selectedAgencyIds;
      level.excludeInternalApproverIds = internalInfo?.excludeIds;
    }

    if (isRfiExternalSelectAll) {
      level.selectedAllExternalApprovers = true;
    } else {
      level.externalUserApproverIds = externalInfo?.individualUserIds;
      level.selectedExternalApproverAgencyIds = externalInfo?.selectedAgencyIds;
      level.excludeExternalUserApproverIds = externalInfo?.excludeIds;
    }

    return [level];
  };

  const onFormSubmit = async () => {
    try {
      setApiError(null);
      const approvalLevelData = buildApprovalLevelData();
      const rfiApprovalLevelData = buildRfiApprovalLevelData();

      if (!approvalLevelData?.length && !rfiApprovalLevelData?.length) {
        toast({
          message: 'No updates made. Please make changes to save.',
          type: 'error',
        });
        return;
      }

      const mutationData = {
        projectEqcTypeId,
        needApproval: true,
      };

      if (rfiApprovalLevelData?.length > 0) {
        mutationData.rfiNeedApproval = true;
      }

      if (approvalLevelData?.length > 0) {
        mutationData.approvalLevelData = approvalLevelData;
      }

      if (rfiApprovalLevelData?.length > 0) {
        mutationData.rfiApprovalLevelData = rfiApprovalLevelData;
      }

      if (isSelectAllAcrossPages) {
        mutationData.isAllSelected = true;
        if (exceptIds?.length > 0) {
          mutationData.exceptProjectEqcTypeStageIds = exceptIds;
        }
      } else {
        mutationData.projectEqcTypeStageIds = selectedStageIds;
      }

      await bulkUpdateApprovers({
        variables: {
          data: mutationData,
        },
      });
    } catch (error) {
      return error;
    }
  };

  const handleCancel = () => {
    if (form.isFieldsTouched()) {
      setShowConfirmationModal(true);
    } else {
      form.resetFields();
      setShowDrawer(false);
    }
  };

  const handleConfirmDiscard = () => {
    form.resetFields();
    setShowDrawer(false);
  };

  return (
    <>
      <CommonDrawer
        title={`Bulk Assign Approvers (${selectedCount} Stages Selected)`}
        open={showDrawer}
        onClose={handleCancel}
        width={1000}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('stage-list-container') || document.body
        }
      >
        <Form form={form} layout="vertical" onFinish={onFormSubmit}>
          <div className="bulk-approvers-step add-approver-modal">
            <ApproverLevels
              form={form}
              projectId={projectId}
              showRfiApprovers={showRfiApprovers}
              highlightedLevels={highlightedLevels}
              setHighlightedLevels={setHighlightedLevels}
              onApproverChange={() => {
                if (apiError) setApiError(null);
                if (highlightedLevels?.length) setHighlightedLevels([]);
              }}
            />
          </div>
          {apiError && (
            <Alert
              title={apiError}
              type="error"
              showIcon
              closable={{ closeIcon: true, onClose: () => setApiError(null) }}
              className="mb-16"
            />
          )}
          <div className="form-buttons">
            <Button
              shape="round"
              className="cancel-button"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              shape="round"
              type="primary"
              className="save-button"
              loading={loading}
              htmlType="submit"
            >
              Save
            </Button>
          </div>
        </Form>
      </CommonDrawer>
      {showConfirmationModal && (
        <CommonBulkConfirmationModal
          showModal={showConfirmationModal}
          setShowModal={setShowConfirmationModal}
          onConfirm={handleConfirmDiscard}
          title="Unsaved Changes"
          message="Your changes are not saved. Do you want to discard them?"
        />
      )}
    </>
  );
};

export default BulkAssignApproversDrawer;
